/*
  <bloom-hero-slider> — premium hero slideshow.
  Crossfade between slides; autoplay pauses on hover, focus and tab-hide;
  arrows, dots, keyboard (←/→) and swipe. Respects prefers-reduced-motion
  (no autoplay). All slides are server-rendered, so it degrades to the first
  slide with JS off. Emits bloom:hero_slide_change for the tracking layer.
*/
class BloomHeroSlider extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.bloom-hero__slide'));
    if (this.slides.length === 0) return;
    this.dots = Array.from(this.querySelectorAll('[data-goto]'));
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.index = 0;
    this.count = this.slides.length;
    this.loop = this.dataset.loop === 'true';
    // In "match the banner image" height mode the section takes the aspect
    // ratio of whichever slide is showing, so no banner is ever cropped.
    this.autoHeight = this.classList.contains('bloom-hero__slider--auto');
    this.syncAspect(0);

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.interval = Math.max(3000, parseInt(this.dataset.interval, 10) || 6000);
    this.canAutoplay = this.dataset.autoplay === 'true' && this.count > 1 && !reduce;

    if (this.count > 1) {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1, true));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1, true));
      this.dots.forEach((d) =>
        d.addEventListener('click', () => this.go(parseInt(d.dataset.goto, 10), true))
      );

      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { this.step(-1, true); }
        else if (e.key === 'ArrowRight') { this.step(1, true); }
      });
      this.setAttribute('tabindex', '0');

      // Pause triggers
      ['mouseenter', 'focusin', 'touchstart'].forEach((ev) =>
        this.addEventListener(ev, () => this.pause(), { passive: true })
      );
      ['mouseleave', 'focusout'].forEach((ev) =>
        this.addEventListener(ev, () => this.play())
      );
      document.addEventListener('visibilitychange', () =>
        document.hidden ? this.pause() : this.play()
      );

      this.initSwipe();
      this.play();
    }
  }

  disconnectedCallback() { this.pause(); }

  go(next, userInitiated) {
    if (next === this.index) return;
    if (next < 0) next = this.loop ? this.count - 1 : 0;
    if (next >= this.count) next = this.loop ? 0 : this.count - 1;

    this.slides.forEach((s, i) => {
      const active = i === next;
      s.classList.toggle('is-active', active);
      s.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
    this.dots.forEach((d, i) => {
      const active = i === next;
      d.classList.toggle('is-active', active);
      d.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    this.index = next;
    this.syncAspect(next);
    this.dispatchEvent(
      new CustomEvent('bloom:hero_slide_change', {
        bubbles: true,
        detail: { index: next, userInitiated: !!userInitiated },
      })
    );
    if (userInitiated) this.play(); // restart the timer after manual nav
  }

  step(dir, userInitiated) { this.go(this.index + dir, userInitiated); }

  syncAspect(i) {
    if (!this.autoHeight) return;
    const slide = this.slides[i];
    if (!slide) return;
    const ar = parseFloat(slide.dataset.aspect);
    const arMobile = parseFloat(slide.dataset.aspectMobile);
    if (ar > 0) this.style.setProperty('--bloom-hero-ar', String(ar));
    if (arMobile > 0) this.style.setProperty('--bloom-hero-ar-mobile', String(arMobile));
  }

  play() {
    this.pause();
    if (!this.canAutoplay) return;
    this.timer = window.setInterval(() => this.step(1, false), this.interval);
  }

  pause() {
    if (this.timer) { window.clearInterval(this.timer); this.timer = null; }
  }

  initSwipe() {
    let startX = 0;
    let tracking = false;
    this.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; tracking = true; }, { passive: true });
    this.addEventListener('touchend', (e) => {
      if (!tracking) return;
      tracking = false;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 45) this.step(dx < 0 ? 1 : -1, true);
    }, { passive: true });
  }
}

if (!customElements.get('bloom-hero-slider')) {
  customElements.define('bloom-hero-slider', BloomHeroSlider);
}
